import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'

const SUBSCRIPTIONS_ORDERS_CACHE = jotai.atom<hooks.Cache<api.Pageable<api.OrderList>[]>>({})

export type SubscriptionsOrdersParams<EXTRA_FIELDS extends string> = {
  extraFields?: EXTRA_FIELDS[]
  limit?: number
  page?: number
  q?: string
  subscriptionsPage?: number
  reverse?: boolean
  reward_id?: string | number | 'all'
  sort?: string
  status?: api.OrderStatus | 'all'
}

type PageableSubscriptionsOrdersList<EXTRA_FIELDS extends string = ''> = api.Pageable<api.OrderList<any, EXTRA_FIELDS>>

type SubscriptionsOrdersConfig<EXTRA_FIELDS extends string = ''> = {
  fetchOnMount?: boolean | 'force'
  params?: SubscriptionsOrdersParams<EXTRA_FIELDS>
}

function toURLSearchParams<EXTRA_FIELDS extends string>(
  params: SubscriptionsOrdersParams<EXTRA_FIELDS> = {},
  searchParams = new URLSearchParams(),
): URLSearchParams {
  if (params === undefined) {
    return searchParams
  }

  if (params.limit) {
    searchParams.set('limit', String(params.limit))
  }
  if (params.subscriptionsPage !== undefined) {
    searchParams.set('offset', String((params.subscriptionsPage - 1) * (params.limit ?? 12)))
  }
  if (params.subscriptionsPage !== undefined) {
    searchParams.set('subscriptionsPage', String(params.subscriptionsPage))
  }

  return searchParams
}

export function useSubscriptionOrders<EXTRA_FIELDS extends string>(
  subscriptionId?: number,
  { params = {}, fetchOnMount = true }: SubscriptionsOrdersConfig<EXTRA_FIELDS> = {},
) {
  const sharedQuery = hooks.useSharedQuery(
    async (subscriptionId: number, params: SubscriptionsOrdersParams<EXTRA_FIELDS> | undefined) => {
      const response = await base.listForSubscription(
        subscriptionId,
        toURLSearchParams({ ...params, limit: params?.limit ?? 12 }),
      )
      return response.next((success) => [success])
    },
    {
      atom: SUBSCRIPTIONS_ORDERS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(subscriptionId, params))

  const response = getResponse()

  return { response: getResponse(), query, queryNext, meta: getLastMeta() }

  function getResponse(): hooks.Response<PageableSubscriptionsOrdersList<EXTRA_FIELDS>[]> {
    if (subscriptionId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return sharedQuery.getCurrentResponse([subscriptionId, params]) as hooks.Response<
      PageableSubscriptionsOrdersList<EXTRA_FIELDS>[]
    >
  }

  function getLastMeta():
    | {
        limit: number
        next: string | null
        total_count: number
        offset?: number | undefined
      }
    | undefined {
    if (response.status === hooks.QueryStatus.PRISTINE) {
      console.warn(`[services/subscription] getLastMeta() can't run if status is pristine`)
      return
    }

    const data = response?.data

    return data?.length ? data[data.length - 1].meta : undefined
  }

  async function query(): Promise<void> {
    if (subscriptionId === undefined) {
      return undefined
    }

    return sharedQuery.query(subscriptionId, params)
  }

  async function queryNext(): Promise<void> {
    if (subscriptionId === undefined) {
      return
    }

    const response = getResponse()
    const next =
      response?.data !== undefined ? (response.data[response.data.length - 1].meta?.next ?? undefined) : undefined
    const nextSearchParam = toURLSearchParams(params, new URLSearchParams(next))

    if (next === undefined) {
      return
    }

    await sharedQuery.patch([subscriptionId, params], async (response) => {
      const ordersResponse = await base.listForSubscription(subscriptionId, nextSearchParam)

      return ordersResponse.next((success) => {
        return [...(response.data ?? []), success]
      })
    })
  }
}

function id<EXTRA_FIELDS extends string>(
  subscriptionId?: number,
  params?: SubscriptionsOrdersParams<EXTRA_FIELDS>,
): string {
  return JSON.stringify([subscriptionId, params?.limit, params?.subscriptionsPage])
}
